import { cn } from "@/lib/utils";

import { pointsReason } from "./fundingText";
import type { Rating, Scale } from "./types";

/** "So bewerten wir": the scoring rules as the fraction publishes them. */
export const FundingScale = ({ scale }: { scale: Scale }) => (
  <section className="flex flex-col gap-6">
    <div className="rounded-lg border bg-card p-5">
      <h2 className="text-base font-semibold text-primary">{scale.titel}</h2>
      <p className="mt-2">
        {scale.zweck.replace("{gesamt}", String(scale.gesamt))}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">{scale.status}</p>
    </div>

    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/40 text-xs text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">Kriterium</th>
            <th className="px-4 py-3 font-medium">Wofür es Punkte gibt</th>
            <th className="px-4 py-3 font-medium">Höchstens</th>
            <th className="px-4 py-3 font-medium">Beispiel</th>
            <th className="px-4 py-3 font-medium">Woher die Daten kommen</th>
          </tr>
        </thead>
        <tbody>
          {scale.fragen.map((f) => (
            <tr key={f.schluessel} className="border-t align-top">
              <td className="px-4 py-3">
                <p className="font-medium">{f.name}</p>
                <p className="text-xs text-muted-foreground">{f.frage}</p>
              </td>
              <td className="px-4 py-3">
                <ul className="flex flex-col gap-1">
                  {f.skala.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </td>
              <td className="px-4 py-3 font-semibold whitespace-nowrap text-primary tabular-nums">
                {f.max} Punkte
              </td>
              <td className="px-4 py-3 text-muted-foreground">{f.beispiel}</td>
              <td className="px-4 py-3 text-muted-foreground">{f.quelle}</td>
            </tr>
          ))}
          <tr className="border-t bg-muted/40">
            <td className="px-4 py-3 font-medium" colSpan={2}>
              Zusammen
            </td>
            <td className="px-4 py-3 font-semibold whitespace-nowrap text-primary tabular-nums">
              {scale.gesamt} Punkte
            </td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>

    <div className="grid gap-4 sm:grid-cols-2">
      {scale.kaesten.map((k) => (
        <div
          key={k.titel}
          className={cn(
            "rounded-lg border p-5",
            k.ton === "warn" ? "border-warning bg-warning-soft" : "bg-card",
          )}
        >
          <h3
            className={cn(
              "text-sm font-semibold",
              k.ton === "warn" ? "text-warning-text" : "text-primary",
            )}
          >
            {k.titel}
          </h3>
          <p className="mt-1 text-sm">{k.text}</p>
        </div>
      ))}
    </div>

    <div className="rounded-lg border bg-card p-5 text-sm">
      <h3 className="font-semibold text-primary">Begriffe</h3>
      <ul className="mt-1 flex flex-col gap-1">
        {scale.begriffe.map((b) => (
          <li key={b.name}>
            <span className="font-medium">{b.name}:</span> {b.text}
          </li>
        ))}
      </ul>
    </div>

    <p className="text-xs text-muted-foreground">{scale.quelle}</p>
  </section>
);

/** Where the points of one application come from. */
export const PointsBreakdown = ({
  scale,
  rating,
}: {
  scale: Scale;
  rating: Rating;
}) => (
  <div className="mt-3 overflow-x-auto rounded-md border bg-muted/40 p-3 text-sm">
    <table className="w-full text-left">
      <tbody>
        {scale.fragen.map((f) => (
          <tr key={f.schluessel} className="align-top">
            <td className="py-1 pr-3 font-medium whitespace-nowrap">
              {f.name}
            </td>
            <td className="py-1 pr-3 font-semibold whitespace-nowrap text-primary tabular-nums">
              {rating[f.schluessel] ?? "–"} von {f.max}
            </td>
            <td className="py-1 text-muted-foreground">
              {pointsReason(f, rating)}
            </td>
          </tr>
        ))}
        <tr className="border-t">
          <td className="py-1 pr-3 font-medium">Zusammen</td>
          <td className="py-1 pr-3 font-semibold whitespace-nowrap text-primary tabular-nums">
            {rating.punkte ?? "–"} von {scale.gesamt}
          </td>
          <td className="py-1 text-muted-foreground">
            {rating.k2 === 0 ? scale.rausHinweis : ""}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);
